import React, { useState, useEffect, useRef } from 'react';
import { TextField } from '@mui/material';
import { useGridPrivateApiContext } from '@mui/x-data-grid/internals';

export function turkishAmountToBackend(value: string): string {
    if (!value) return '';
    // Binlik ayraç noktalarını kaldır
    const withoutDots = value.replace(/\./g, '');
    // Ondalık virgülü noktaya çevir
    const withDot = withoutDots.replace(',', '.');
    // Sondaki nokta kaldır ("2." → "2")
    const withoutTrailingDot = withDot.replace(/\.$/, '');
    // Her zaman 2 ondalık basamakla gönder: "34,00" → "34.00", "1,10" → "1.10"
    const num = parseFloat(withoutTrailingDot);
    if (isNaN(num)) return withoutTrailingDot;
    return num.toFixed(2);
}

function formatFilterAmount(digits: string): string {
    if (!digits) return '';
    let numeric = digits.replace(/\D/g, '');
    if (!numeric) return '';
    numeric = numeric.padStart(3, '0');
    const integerPart = numeric.slice(0, -2);
    const decimalPart = numeric.slice(-2);
    const integerFormatted = new Intl.NumberFormat('tr-TR').format(BigInt(integerPart));
    return `${integerFormatted},${decimalPart}`;
}

/** Maksimum filtre tutarı: 9.999.999.999,99 (10 tam + 2 ondalık hane) Yeni standart belirlenirse buradan kolayca değiştirebiliriz */
export const FILTER_AMOUNT_MAX_DIGITS = 12;
export const FILTER_AMOUNT_MAX_DISPLAY = '9.999.999.999,99';

const createAmountFilterInput = (maxDigits: number) => {
    const AmountFilterInput = (props: any) => {
        const { item, applyValue, hasFocus } = props;
        const inputRef = useRef<HTMLInputElement>(null);
        const skipAutoSelectionRef = useRef(false);
        const apiRef = useGridPrivateApiContext();

        const clearDataGridFocus = () => {
            const api = apiRef as React.MutableRefObject<any>;
            if (api?.current?.setState) {
                api.current.setState((state: any) => ({
                    ...state,
                    focus: { ...state.focus, columnHeaderFilter: null },
                }));
                api.current.forceUpdate?.();
            }
        };

        useEffect(() => {
            if (hasFocus) {
                inputRef.current?.focus({ preventScroll: true });
            }
        }, [hasFocus]);

        const [displayValue, setDisplayValue] = useState<string>(() =>
            formatFilterAmount(String(item?.value ?? '').replace(/\D/g, ''))
        );

        useEffect(() => {
            setDisplayValue(formatFilterAmount(String(item?.value ?? '').replace(/\D/g, '')));
        }, [item?.value]);

        const commit = (formatted: string) => {
            setDisplayValue(formatted);
            applyValue({ ...item, value: formatted });
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const input = e.target as HTMLInputElement;
            const digits = input.value.replace(/\D/g, '');
            if (digits.length > maxDigits) return;
            const formatted = formatFilterAmount(input.value);
            commit(formatted);
            requestAnimationFrame(() => {
                inputRef.current?.setSelectionRange(formatted.length, formatted.length);
            });
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault();
            const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, maxDigits);
            if (!digits) return;
            const formatted = formatFilterAmount(digits);
            commit(formatted);
            const input = e.target as HTMLInputElement;
            requestAnimationFrame(() => {
                input.setSelectionRange(formatted.length, formatted.length);
            });
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            e.stopPropagation();
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                const input = e.target as HTMLInputElement;
                const value = input.value;
                const cursorPos = input.selectionStart ?? value.length;
                const selEnd = input.selectionEnd ?? cursorPos;
                const digits = value.replace(/\D/g, '');
                let newDigits: string;
                if (selEnd > cursorPos) {
                    const digitsBeforeStart = value.slice(0, cursorPos).replace(/\D/g, '').length;
                    const digitsBeforeEnd = value.slice(0, selEnd).replace(/\D/g, '').length;
                    newDigits = digits.slice(0, digitsBeforeStart) + digits.slice(digitsBeforeEnd);
                } else if (e.key === 'Backspace') {
                    let digitIndex = -1;
                    for (let i = cursorPos - 1; i >= 0; i--) {
                        if (/\d/.test(value[i])) {
                            digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                            break;
                        }
                    }
                    if (digitIndex === -1) return;
                    newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                } else {
                    let digitIndex = -1;
                    for (let i = cursorPos; i < value.length; i++) {
                        if (/\d/.test(value[i])) {
                            digitIndex = value.slice(0, i).replace(/\D/g, '').length;
                            break;
                        }
                    }
                    if (digitIndex === -1) return;
                    newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
                }
                const nonZero = newDigits.replace(/^0+/, '');
                const formatted = nonZero ? formatFilterAmount(newDigits) : '';
                commit(formatted);
                requestAnimationFrame(() => {
                    input.setSelectionRange(formatted.length, formatted.length);
                });
                return;
            }
            const allowedKeys = ['Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
            if (!allowedKeys.includes(e.key) && (e.key < '0' || e.key > '9') && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
            }
        };

        const selectAll = (input: HTMLInputElement | null | undefined) => {
            if (!input) return;
            input.setSelectionRange(0, input.value.length);
        };

        const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
            if (e.detail >= 2) {
                skipAutoSelectionRef.current = true;
            }
        };

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            const input = e.target as HTMLInputElement;
            requestAnimationFrame(() => {
                if (skipAutoSelectionRef.current) {
                    skipAutoSelectionRef.current = false;
                    selectAll(inputRef.current ?? input);
                    return;
                }
                if (hasFocus) {
                    const el = inputRef.current ?? input;
                    if (!el) return;
                    el.setSelectionRange(el.value.length, el.value.length);
                }
            });
            clearDataGridFocus();
        };

        const handleDoubleClick = () => {
            requestAnimationFrame(() => {
                selectAll(inputRef.current);
            });
        };

        return (
            <TextField
                inputRef={inputRef}
                value={displayValue}
                onChange={handleChange}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                onMouseDown={handleMouseDown}
                onFocus={handleFocus}
                onDoubleClick={handleDoubleClick}
                size="small"
                variant="standard"
                inputProps={{ inputMode: 'decimal' }}
                sx={{ '& input': { fontSize: '0.875rem' } }}
            />
        );
    };
    return AmountFilterInput;
};

const makeGetApplyFilterFn =
    (comparator: (cellVal: number, filterVal: number) => boolean) =>
        (filterItem: any) => {
            if (
                filterItem.value === undefined ||
                filterItem.value === null ||
                filterItem.value === ''
            )
                return null;
            const filterNum = parseFloat(turkishAmountToBackend(String(filterItem.value)));
            if (isNaN(filterNum)) return null;
            return ({ value }: any) => {
                const num = typeof value === 'number' ? value : parseFloat(String(value));
                if (isNaN(num)) return false;
                return comparator(num, filterNum);
            };
        };

export const createFilterOperatorsAmount = (maxDigits = 16) => {
    const InputComponent = createAmountFilterInput(maxDigits);
    return [
        {
            label: 'Eşittir',
            value: '=',
            isAmount: true,
            InputComponent,
            getApplyFilterFn: makeGetApplyFilterFn((a, b) => a === b),
        },
        {
            label: 'Eşit değil',
            value: '!=',
            isAmount: true,
            InputComponent,
            getApplyFilterFn: makeGetApplyFilterFn((a, b) => a !== b),
        },
        {
            label: 'Büyüktür',
            value: '>',
            isAmount: true,
            InputComponent,
            getApplyFilterFn: makeGetApplyFilterFn((a, b) => a > b),
        },
        {
            label: 'Büyük veya eşit',
            value: '>=',
            isAmount: true,
            InputComponent,
            getApplyFilterFn: makeGetApplyFilterFn((a, b) => a >= b),
        },
        {
            label: 'Küçüktür',
            value: '<',
            isAmount: true,
            InputComponent,
            getApplyFilterFn: makeGetApplyFilterFn((a, b) => a < b),
        },
        {
            label: 'Küçük veya eşit',
            value: '<=',
            isAmount: true,
            InputComponent,
            getApplyFilterFn: makeGetApplyFilterFn((a, b) => a <= b),
        },
    ];
};

/** Varsayılan tutar filtre operatörleri (16 hane) */
export const filterOperatorsAmount = createFilterOperatorsAmount();

/** Tutar / ücret filtreleri — maks. 999.999.999.999,99 */
export const filterOperatorsAmountMax999M = createFilterOperatorsAmount(FILTER_AMOUNT_MAX_DIGITS);
